// SECURITY: Centralized environment configuration
// Replaces all hardcoded URLs with environment variables

interface EnvironmentConfig {
  supabase: {
    url: string;
    anonKey: string;
    storageUrl: string;
  };
  assets: {
    logoLight: string;
    logoDark: string;
    baseUrl: string;
  };
  api: {
    baseUrl: string;
  };
  app: {
    environment: 'development' | 'production';
    debugMode: boolean;
  };
}

const resolveAppEnvironment = (): 'development' | 'production' => {
  const configuredEnvironment = String(import.meta.env.VITE_ENVIRONMENT || '').trim().toLowerCase();
  if (configuredEnvironment === 'production') return 'production';
  if (configuredEnvironment === 'development') return 'development';
  return import.meta.env.PROD ? 'production' : 'development';
};

const resolvedEnvironment = resolveAppEnvironment();
const debugModeFromEnv = String(import.meta.env.VITE_DEBUG_MODE || '').trim().toLowerCase() === 'true';

// Validate environment variables
const validateEnvVar = (key: string, fallback?: string): string => {
  const value = import.meta.env[key] || fallback;
  if (!value) {
    console.error(`🚨 SECURITY: Missing environment variable: ${key}`);
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
};

// SECURITY: All URLs must come from environment variables
export const env: EnvironmentConfig = {
  supabase: {
    url: validateEnvVar('VITE_SUPABASE_URL'),
    anonKey: validateEnvVar('VITE_SUPABASE_ANON_KEY'),
    storageUrl: validateEnvVar('VITE_SUPABASE_STORAGE_URL', 
      `${validateEnvVar('VITE_SUPABASE_URL')}/storage/v1/object/public`
    ),
  },
  assets: {
    baseUrl: validateEnvVar('VITE_SUPABASE_STORAGE_URL', 
      `${validateEnvVar('VITE_SUPABASE_URL')}/storage/v1/object/public/assets`
    ),
    logoLight: validateEnvVar('VITE_LOGO_LIGHT', 'NovelSprint7.png'),
    logoDark: validateEnvVar('VITE_LOGO_DARK', 'NovelSprintDM3.png'),
  },
  api: {
    baseUrl: validateEnvVar('VITE_API_BASE_URL', 'http://localhost:3005'),
  },
  app: {
    environment: resolvedEnvironment,
    debugMode: debugModeFromEnv || import.meta.env.DEV,
  },
};

// SECURITY: Asset URL builders (prevent hardcoded URLs)
export const getAssetUrl = (filename: string, folder: string = 'assets'): string => {
  const baseUrl = env.supabase.storageUrl.endsWith('/') 
    ? env.supabase.storageUrl.slice(0, -1) 
    : env.supabase.storageUrl;
  const cleanFolder = folder.startsWith('/') ? folder.slice(1) : folder;
  const cleanFilename = filename.startsWith('/') ? filename.slice(1) : filename;
  
  return `${baseUrl}/${cleanFolder}/${cleanFilename}`;
};

// Logo URL cache to prevent multiple requests for the same logo
const logoUrlCache = new Map<'light' | 'dark', string>();

export const getLogoUrl = (variant: 'light' | 'dark' = 'light'): string => {
  // Return cached URL if already computed
  if (logoUrlCache.has(variant)) {
    return logoUrlCache.get(variant)!;
  }

  const filename = variant === 'light' ? env.assets.logoLight : env.assets.logoDark;
  const url = getAssetUrl(filename, 'assets');
  
  // Cache the URL for future calls
  logoUrlCache.set(variant, url);
  
  return url;
};

// SECURITY: API URL builders
export const getApiUrl = (endpoint: string): string => {
  return `${env.api.baseUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
};

// SECURITY: Supabase URL builders
export const getSupabaseUrl = (path: string): string => {
  return `${env.supabase.url}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Export environment info for debugging
export const getEnvironmentInfo = () => ({
  environment: env.app.environment,
  debugMode: env.app.debugMode,
  hasSupabaseUrl: !!env.supabase.url,
  hasSupabaseKey: !!env.supabase.anonKey,
  apiBaseUrl: env.api.baseUrl,
});

// SECURITY: Runtime validation
if (env.app.environment === 'production') {
  // In production, ensure no debug mode
  if (env.app.debugMode) {
    console.warn('⚠️ SECURITY: Debug mode enabled in production!');
  }
  
  // Validate critical environment variables in production
  if (!env.supabase.url || !env.supabase.anonKey) {
    throw new Error('🚨 SECURITY: Missing critical Supabase configuration in production');
  }
}

export default env; 
